import React, { useEffect, useRef } from 'react';
import { Theme, Typography, Box } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import AdyenCheckout from '@adyen/adyen-web';
import { TFunction } from 'i18next';
import { useAdyen } from 'common/hooks/useAdyen';

interface Props {
	t: TFunction;
	adyenCheckout: AdyenCheckout;
	paymentError: string | null;
}

const AdyenCardElement = (props: Props) => {
	const classes = useStyles();
	const { setMethodSubmitAction } = useAdyen();
	const { adyenCheckout, t, paymentError } = props;
	const paymentElementRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const renderPaymentForm = () => {
			const element = adyenCheckout
				.create('scheme', {
					brands: ['mc', 'visa'],
					hasHolderName: true,
					holderNameRequired: true,
				})
				.mount(paymentElementRef.current!);
			setMethodSubmitAction('CARD_ONLINE', element.submit);
		};
		renderPaymentForm();
		// Adyen payment component should not be be re-rendered: https://docs.adyen.com/checkout/components-web
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div>
			<div className={classes.wrapper}>
				<div ref={paymentElementRef}></div>
			</div>
			{paymentError ? (
				<Box mt={2}>
					<Typography color="error" align="center">
						{t('common:payment.verificationError', 'Verification failed')}
					</Typography>
				</Box>
			) : null}
		</div>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		wrapper: {
			position: 'relative',
			fontSize: '1.4rem',
			fontFamily: theme.typography.fontFamily,
			fontWeight: 400,
		},
		'@global': {
			'.adyen-checkout__label__text': {
				fontSize: '1.4rem',
			},
			'.adyen-checkout__label__text:not(.adyen-checkout__label__text--error)': {
				color: theme.palette.text.primary,
			},
			'.adyen-checkout__error-text': {
				fontSize: '1.2rem',
			},
			'.adyen-checkout__input:not(.adyen-checkout__input--error)': {
				border: `1px solid ${theme.palette.border.inputOutline}`,
			},
			'.adyen-checkout__button': {
				borderRadius: 50,
				minWidth: 112,
				minHeight: 40,
				fontWeight: 700,
				fontFamily: theme.typography.fontFamily,
				fontSize: '1.4rem',
				boxShadow:
					'0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
				backgroundColor: theme.palette.primary.main,
				color: theme.palette.primary.contrastText,
			},
			'.adyen-checkout__button:hover': {
				boxShadow:
					'0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
				backgroundColor: theme.palette.primary.dark,
			},
		},
	}),
);

export default AdyenCardElement;
